/**
 * Phase 18 — Push notification settings page.
 *
 * Lives at `/profile/notifications`. The user can:
 *
 *   1. See their current state (Off / On / Blocked-by-browser /
 *      Needs-iOS-install) and toggle subscribe/unsubscribe.
 *   2. See every active subscription (one row per device/browser),
 *      with a per-row revoke button — useful for "I lost my old
 *      phone, kill its push token" scenarios.
 *   3. Send themselves a test push to confirm end-to-end delivery
 *      (rate-limited server-side to 3/hour).
 *
 * This page is intentionally a single self-contained component (no
 * shared shell) so it stays readable; the styling apes the existing
 * `Profile*` pages so it visually slots into the same hub.
 */
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { customerFetch } from "../lib/customerFetch";
import {
  isIOSSafariNonStandalone,
  isPushSupported,
  requestPermissionAndSubscribe,
  unsubscribe,
} from "../lib/push-notifications";
import { BottomNav } from "../components/BottomNav";

interface SubscriptionRow {
  id: string;
  endpoint: string;
  platformHint: string | null;
  userAgent: string | null;
  subscribedAt: string;
  lastUsedAt: string | null;
}

type PageState =
  | { kind: "loading" }
  | { kind: "ready"; subs: SubscriptionRow[]; permission: NotificationPermission }
  | { kind: "error"; message: string };

export function ProfileNotificationsPage() {
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [busy, setBusy] = useState<null | "subscribe" | "unsubscribe" | "test" | string>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showIosHelp, setShowIosHelp] = useState(false);

  async function refresh() {
    if (!isPushSupported()) {
      setState({
        kind: "error",
        message:
          "This browser doesn't support web push notifications. Try the latest Chrome, Edge, Firefox, or Safari.",
      });
      return;
    }
    const res = await customerFetch<{ subscriptions: SubscriptionRow[] }>(
      "/api/v1/customer/me/push/subscriptions",
    );
    if (!res.ok) {
      setState({ kind: "error", message: res.message });
      return;
    }
    setState({
      kind: "ready",
      subs: res.data.subscriptions,
      permission: Notification.permission,
    });
  }

  useEffect(() => {
    void refresh();
  }, []);

  function showToast(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3500);
  }

  async function onSubscribe() {
    if (isIOSSafariNonStandalone()) {
      setShowIosHelp(true);
      return;
    }
    setBusy("subscribe");
    const r = await requestPermissionAndSubscribe();
    setBusy(null);
    if (r.ok) {
      showToast("Notifications enabled.");
      void refresh();
      return;
    }
    if (r.errorCode === "PERMISSION_DENIED") {
      showToast(
        "You blocked notifications. Re-enable them in your browser site settings.",
      );
    } else {
      showToast(r.errorMessage ?? "Couldn't enable notifications.");
    }
    void refresh();
  }

  async function onUnsubscribe() {
    setBusy("unsubscribe");
    const r = await unsubscribe();
    setBusy(null);
    if (r.ok) {
      showToast("Notifications turned off on this device.");
    } else {
      showToast(r.message ?? "Couldn't turn off notifications.");
    }
    void refresh();
  }

  async function onTestPush() {
    setBusy("test");
    const r = await customerFetch<{ delivered: number; attempted: number }>(
      "/api/v1/customer/me/push/test",
      {
        method: "POST",
        body: JSON.stringify({
          title: "Orbit test",
          body: "If you can read this, push is working.",
        }),
      },
    );
    setBusy(null);
    if (!r.ok) {
      if (r.status === 429) {
        showToast("Test push rate-limited — try again in an hour.");
      } else {
        showToast(r.message);
      }
      return;
    }
    showToast(
      r.data.attempted === 0
        ? "No active subscriptions on this account."
        : `Test push sent (delivered to ${r.data.delivered}/${r.data.attempted} devices).`,
    );
  }

  async function onRevokeOne(id: string) {
    setBusy(id);
    const r = await customerFetch(
      `/api/v1/customer/me/push/subscriptions/${id}`,
      { method: "DELETE" },
    );
    setBusy(null);
    if (!r.ok) {
      showToast(r.message);
    } else {
      showToast("Device revoked.");
    }
    void refresh();
  }

  return (
    <div className="min-h-screen pb-28">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center gap-3">
          <Link href="/profile" className="text-navy text-sm" aria-label="Back to profile">
            ← Back
          </Link>
          <h1 className="text-xl font-bold text-navy">Notifications</h1>
        </header>

        {state.kind === "loading" && (
          <p className="mt-6 text-sm text-ink-dark">Loading…</p>
        )}

        {state.kind === "error" && (
          <div className="mt-6 card">
            <p className="text-red-600">{state.message}</p>
          </div>
        )}

        {state.kind === "ready" && (
          <>
            <section className="mt-6 card">
              <h2 className="text-sm font-semibold text-navy">Status</h2>
              <p className="mt-1 text-sm text-ink-dark">
                {state.permission === "denied" ? (
                  <>You've blocked Orbit notifications in your browser.</>
                ) : state.subs.length > 0 ? (
                  <>You'll get push notifications on this and other approved devices.</>
                ) : (
                  <>Push notifications are off for this account.</>
                )}
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                {state.subs.length === 0 ? (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={onSubscribe}
                    disabled={busy !== null || state.permission === "denied"}
                  >
                    {busy === "subscribe" ? "Working…" : "Turn on for this device"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={onUnsubscribe}
                    disabled={busy !== null}
                  >
                    {busy === "unsubscribe" ? "Working…" : "Turn off on this device"}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onTestPush}
                  disabled={busy !== null || state.subs.length === 0}
                >
                  {busy === "test" ? "Sending…" : "Send test push"}
                </button>
              </div>
            </section>

            <section className="mt-6">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-dark/60">
                Approved devices
              </h2>
              <div className="mt-2 card divide-y divide-navy/5 p-0 overflow-hidden">
                {state.subs.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-ink-dark">
                    No devices approved yet.
                  </p>
                ) : (
                  state.subs.map((s) => (
                    <div key={s.id} className="flex items-start gap-3 px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-navy truncate">
                          {labelForPlatform(s.platformHint, s.userAgent)}
                        </p>
                        <p className="mt-0.5 text-[11px] text-ink-dark/70">
                          Subscribed {new Date(s.subscribedAt).toLocaleDateString()}
                          {s.lastUsedAt
                            ? ` · last used ${new Date(s.lastUsedAt).toLocaleDateString()}`
                            : ""}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => onRevokeOne(s.id)}
                        disabled={busy !== null}
                        className="text-xs text-red-600 hover:underline"
                      >
                        {busy === s.id ? "…" : "Revoke"}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </section>
          </>
        )}

        {showIosHelp && (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4"
            onClick={() => setShowIosHelp(false)}
          >
            <div
              className="w-full max-w-md rounded-2xl bg-white p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-base font-semibold text-navy">
                Install Orbit to your home screen first
              </h3>
              <p className="mt-2 text-sm text-ink-dark">
                Apple only delivers push notifications to home-screen
                installed PWAs. Tap the Share button below, then choose{" "}
                <strong>Add to Home Screen</strong>. Open Orbit from
                that icon and try again.
              </p>
              <button
                type="button"
                onClick={() => setShowIosHelp(false)}
                className="btn-primary mt-4 w-full"
              >
                Got it
              </button>
            </div>
          </div>
        )}

        {toast && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 rounded-full bg-navy px-4 py-2 text-sm text-white shadow-lg">
            {toast}
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}

function labelForPlatform(
  hint: string | null,
  ua: string | null,
): string {
  if (hint === "FCM") return "Chromium device (Chrome, Edge, Brave)";
  if (hint === "MOZILLA") return "Firefox";
  if (hint === "APPLE") return "Apple device (iOS / Safari)";
  if (hint === "WEB_PUSH") return "Browser device";
  if (ua) return ua.slice(0, 60);
  return "Unknown device";
}
